import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { IcraaComplianceContext } from '../../IcraaComplianceContext';
import { getApplicantPath } from '../../../constants/urls';
import { getPageLocationForOverviewSection } from '../../../utils/route';
import { useDispatch, useSelector } from 'react-redux';
import { retrieveSharedReports } from '../../../actions/sharedReportsActionCreators';
import { getHasRetrievedSharedReportsSelector } from '../../../selectors/sharedReports';
import { getApplicationManagerNeedsToIdv, listingAliasSelector } from '../../../selectors';
import { requestApplicationManagerToIdv } from '../../../actions/initApplicationActionCreators';
import useQueryVariables from '../../../hooks/useQueryVariables';
import { GET_ICRAA_PAGE_CONFIG_QUERY } from '../../../utils/complianceMessaging';
import { useApplicationSummaryContext } from '../../ApplicationSummaryContext';

/**
 * This component provides the values that drive legal compliance. It mainly handles two things
 * 1. The open status of the ICRAA acceptance modal
 * 2. Fetching the ICRAA compliance page config
 * This is mainly used by the LegalComplianceWall component
 *
 * @typedef {Object} IcraaPageConfigProvider
 * @property isLoading - Is the request for ICRAA compliance page config loading
 * @property hasComplianceError - Has the request for ICRAA compliance page config failed
 * @property icraaPageConfig - If the request is successful , the page config to render content for the legal compliance wall
 * @property icraaAcceptedForApplicant - Has ICRAA been accepted for the current applicant
 * @property isOpen - Is the legal compliance wall modal open
 * @property setIsOpen - Change the open status of the legal compliance wall
 * @property onNavLinkClick - Callback when the landlord clicks on a section nav link
 */
export default function IcraaPageConfigProvider({ applicant, children, ...rest }) {
  const dispatch = useDispatch();
  const { applicationId } = useQueryVariables();
  const listingAlias = useSelector(listingAliasSelector);
  const { userId: applicantId, zgAuthZone } = applicant;
  const hasRetrievedSharedReports = useSelector(getHasRetrievedSharedReportsSelector(applicantId));
  const history = useHistory();
  const [isOpen, setIsOpen] = useState(false);
  const [sectionId, setSectionId] = useState('');
  const [navigationCallback, setNavigationCallback] = useState(null);
  const [isRetrievingSharedReports, setIsRetrievingSharedReports] = useState(false);
  const applicationManagerNeedsToIdv = useSelector(getApplicationManagerNeedsToIdv);
  const { isApplicationDeclined } = useApplicationSummaryContext();
  const { loading, error, data } = useQuery(GET_ICRAA_PAGE_CONFIG_QUERY, {
    variables: {
      propertyId: listingAlias,
      applicationId,
    },
  });
  const hasComplianceError = Boolean(!loading && error);
  const icraaPageConfig = data?.property?.application?.icraaPageConfig ?? null;
  const icraaAcceptedForApplicant = icraaPageConfig ? icraaPageConfig.hasBeenAcceptedFor.includes(applicantId) : false;

  /**
   * If this handler is set, then it means we're fetching the page
   * config after a mutation. In which case, check if compliance is
   * still needed, and if not, call the handler.
   */
  if (navigationCallback && !loading && icraaPageConfig && icraaAcceptedForApplicant) {
    navigationCallback();
    setNavigationCallback(null);
  }

  useEffect(() => {
    if (
      !isRetrievingSharedReports &&
      !hasRetrievedSharedReports &&
      icraaAcceptedForApplicant &&
      !isApplicationDeclined
    ) {
      // The call to retrieve shared reports needs ICRAA compliance.
      dispatch(
        retrieveSharedReports({
          applicantId,
          applicationId,
          propertyId: listingAlias,
        }),
      );
      setIsRetrievingSharedReports(true);
    }
  }, [
    isRetrievingSharedReports,
    hasRetrievedSharedReports,
    icraaAcceptedForApplicant,
    isApplicationDeclined,
    sectionId,
    dispatch,
    applicantId,
    applicationId,
    listingAlias,
  ]);

  function onNavLinkClick({ sectionId: nextSectionId, onAllCompliantCallback, setCustomNavigationCallback }) {
    if (applicationManagerNeedsToIdv) {
      dispatch(requestApplicationManagerToIdv());
    }

    const isIcraaApplicableToCurrentSection = icraaPageConfig?.appliesTo?.includes(nextSectionId);
    setSectionId(nextSectionId);

    if (setCustomNavigationCallback || (!icraaAcceptedForApplicant && isIcraaApplicableToCurrentSection)) {
      // If ICRAA needs to be accepted for applicant, open the legal compliance wall.
      if (!icraaAcceptedForApplicant) {
        setIsOpen(true);
      }

      /**
       * If compliance is needed, or if client needs to dictate the terms
       * for when navigation happens, then set this navigation callback.
       */
      (setCustomNavigationCallback || setNavigationCallback)(
        /**
         * This explains why we're passing a function that returns a
         * function.
         * https://github.com/facebook/react/issues/15279
         */
        () => () => {
          history.push(
            getApplicantPath({
              propertyId: listingAlias,
              applicationId,
              pageLocation: getPageLocationForOverviewSection(nextSectionId),
              zgAuthZone,
              applicantId,
            }),
          );
          onAllCompliantCallback();
        },
      );
    } else {
      /**
       * Only if ICRAA compliance is not needed and custom navigation is not
       * specified, then button is an anchor with href.
       */
      onAllCompliantCallback();
    }
  }

  useEffect(() => {
    if (hasRetrievedSharedReports) {
      setIsRetrievingSharedReports(false);
    }
  }, [hasRetrievedSharedReports]);

  return (
    <IcraaComplianceContext.Provider
      value={{
        isLoading: loading,
        hasComplianceError,
        icraaPageConfig,
        icraaAcceptedForApplicant,
        isOpen,
        setIsOpen,
        onNavLinkClick,
        onCancelCleanup() {
          setNavigationCallback(null);
        },
        ...rest,
      }}
    >
      {children}
    </IcraaComplianceContext.Provider>
  );
}

IcraaPageConfigProvider.propTypes = {
  applicant: PropTypes.shape({
    userId: PropTypes.string.isRequired,
    zgAuthZone: PropTypes.string.isRequired,
  }),
  children: PropTypes.node,
};
